const idOf = item => String(item?.card?._id ?? '');

export function restoreStudyQueue(cards, cachedQueue, saved, pendingReviewIds = []) {
  const oldIds = Array.isArray(saved?.cardIds) ? saved.cardIds.map(String) : [];
  const oldIndex = Number.isInteger(saved?.queueIndex) ? Math.max(0, saved.queueIndex) : 0;
  const answers = saved?.answers || {};
  const pendingIds = new Set(pendingReviewIds.map(String));

  if (Array.isArray(cachedQueue) && cachedQueue.length > 0) {
    const index = Math.min(oldIndex, cachedQueue.length);
    if (!Array.isArray(cards)) {
      return { list: cachedQueue, idx: index, saved: answers, stats: saved?.stats };
    }

    const completed = cachedQueue.slice(0, index);
    const completedIds = new Set(completed.map(idOf));
    const serverById = new Map(cards.map(item => [idOf(item), item]));
    const future = [];
    const keptFutureIds = new Set();
    const keptRequeues = new Set();

    cachedQueue.slice(index).forEach(item => {
      const id = idOf(item);
      const fresh = serverById.get(id);
      if (fresh) {
        future.push(fresh);
        keptFutureIds.add(id);
      } else if (completedIds.has(id) && !keptRequeues.has(id)) {
        future.push(item);
        keptRequeues.add(id);
      }
    });

    cards.forEach(item => {
      const id = idOf(item);
      if (!keptFutureIds.has(id) && (!completedIds.has(id) || !pendingIds.has(id))) future.push(item);
    });

    return { list: [...completed, ...future], idx: completed.length, saved: answers, stats: saved?.stats };
  }

  const serverById = new Map((cards || []).map(item => [idOf(item), item]));
  const list = [];
  const oldToNew = new Map();
  const includedIds = new Set();
  oldIds.forEach((id, oldPosition) => {
    const item = serverById.get(id);
    if (!item) return;
    oldToNew.set(oldPosition, list.length);
    list.push(item);
    includedIds.add(id);
  });

  const currentCardId = String(saved?.currentCardId || oldIds[oldIndex] || '');
  const occurrence = Number.isInteger(saved?.currentOccurrence)
    ? saved.currentOccurrence
    : oldIds.slice(0, oldIndex + 1).filter(id => id === currentCardId).length - 1;
  let seen = -1;
  const identityPosition = currentCardId
    ? oldIds.findIndex(id => id === currentCardId && ++seen === occurrence)
    : -1;
  const activeOldPosition = identityPosition >= 0 ? identityPosition : oldIndex;
  const savedFutureIds = new Set(oldIds.slice(activeOldPosition));
  const completedDueIds = new Set(oldIds.slice(0, activeOldPosition).filter(id => (
    serverById.has(id) && !pendingIds.has(id) && !savedFutureIds.has(id)
  )));
  (cards || []).forEach(item => {
    const id = idOf(item);
    if (!includedIds.has(id)) {
      list.push(item);
      includedIds.add(id);
    } else if (completedDueIds.has(id)) {
      list.push(item);
      completedDueIds.delete(id);
    }
  });

  const cursorPosition = oldIds.findIndex((id, position) => (
    position >= activeOldPosition && serverById.has(id)
  ));
  const cursor = cursorPosition < 0 ? oldToNew.size : (oldToNew.get(cursorPosition) ?? 0);
  const restoredAnswers = {};
  Object.entries(answers).forEach(([position, answer]) => {
    const newPosition = oldToNew.get(Number(position));
    if (newPosition !== undefined && newPosition < cursor) restoredAnswers[newPosition] = answer;
  });
  return { list, idx: cursor, saved: restoredAnswers, stats: saved?.stats };
}
